/**
 * Start/stop and quick-log, on the task row itself.
 *
 * The quick-log bar already has a task picker, which is why these were left out
 * originally — the argument being that per-row buttons were redundant clutter.
 * A week of real use disproved it: when you are looking at a task, that is the
 * moment you want to time it, and making the round trip through a picker in the
 * header is enough friction that the time does not get logged at all.
 *
 * Hidden until hover on a pointer device so a list of thirty rows is not thirty
 * pairs of buttons; always visible on touch, which has no hover.
 */

import { useState } from "react";

import { PlayIcon, StopIcon, StopwatchIcon } from "@/components/NavIcons";
import { useToast } from "@/context/ToastContext";
import { useTaskTime } from "@/hooks/useTaskTime";
import { parseDuration } from "@/lib/timeRollup";
import type { Item } from "@/lib/types";

export const QUICK_MINUTES = [15, 30, 60];

export function TaskTimeControls({ item, mobile }: { item: Item; mobile: boolean }) {
  const toast = useToast();
  const { timingThis, toggleTimer, logMinutes } = useTaskTime(item);
  const [menu, setMenu] = useState(false);
  const [custom, setCustom] = useState("");

  async function log(minutes: number) {
    setMenu(false);
    setCustom("");
    await logMinutes(minutes);
  }

  return (
    <span
      className={timingThis ? undefined : "row-time-controls"}
      style={{ display: "inline-flex", alignItems: "center", gap: 3, flex: "none", position: "relative" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={(e) => { e.stopPropagation(); void toggleTimer(); }}
        title={timingThis ? "Stop and log" : "Start a timer on this task"}
        aria-label={timingThis ? "Stop timer" : "Start timer"}
        style={{ ...tap(mobile), color: timingThis ? "var(--accent)" : "var(--muted2)" }}
      >
        {timingThis ? <StopIcon size={mobile ? 15 : 13} /> : <PlayIcon size={mobile ? 15 : 13} />}
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}
        title="Log time against this task"
        aria-label="Log time"
        style={tap(mobile)}
      >
        <StopwatchIcon size={mobile ? 18 : 16} />
      </button>

      {menu && (
        <>
          <span onClick={() => setMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <span style={{
            position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 41,
            display: "flex", alignItems: "center", gap: 4, padding: 5,
            background: "var(--surface)", border: "1px solid var(--line2)",
            borderRadius: 999, boxShadow: "var(--shadowHover)",
          }}>
            {QUICK_MINUTES.map((m) => (
              <button key={m} onClick={() => void log(m)} style={chip}>
                {m < 60 ? `${m}m` : `${m / 60}h`}
              </button>
            ))}
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const mins = parseDuration(custom);
                if (mins === null || mins <= 0) { toast("Try 1:30, 90 or 1.5h"); return; }
                void log(mins);
              }}
              placeholder="1:30"
              aria-label="Custom duration"
              style={{
                width: 56, padding: "3px 7px", borderRadius: 999, fontSize: 11.5, textAlign: "center",
                border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
              }}
            />
          </span>
        </>
      )}
    </span>
  );
}

const tap = (mobile: boolean): React.CSSProperties => ({
  width: mobile ? 32 : 24, height: mobile ? 32 : 24,
  display: "flex", alignItems: "center", justifyContent: "center",
  borderRadius: 999, border: "none", background: "none",
  color: "var(--muted2)", cursor: "pointer", fontSize: 11, padding: 0,
});
const chip: React.CSSProperties = {
  padding: "3px 9px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 11.5, cursor: "pointer",
};
